// Iter 131 — Account page restructured into a tabbed layout.
//
// Previously this page stacked 10 full-width cards vertically (3,629px
// scroll height) which felt endless.  Now grouped into 4 tabs:
//
//   • Profile        — identity, quick stats, Earnings + Slips shortcuts
//   • Betway         — Tested regions + credentials + diagnostic
//   • Notifications  — Goal alerts + Email subscriptions
//   • System         — Sanity flags + Stream manager + recent parlays
//
// Result: ~700-900px per tab, no more endless scrolling.
import { useEffect, useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";
import TestedRegions from "../components/TestedRegions";
import SanityFlags from "../components/SanityFlags";
import StreamManager from "../components/StreamManager";
import GoalAlertSettings from "../components/GoalAlertSettings";
import EmailSubscriptions from "../components/EmailSubscriptions";
import TVCountryPicker from "../components/TVCountryPicker";
import TopNav from "../components/TopNav";
import StrategyPanel from "../components/StrategyPanel";
import AliasReviewWidget from "../components/AliasReviewWidget";
import {
  KeyRound, Trash2, LogOut, CheckCircle2, AlertCircle, Clock, ShieldCheck,
  Activity, BarChart3, Wallet, Sparkles, Bell, Wrench, UserCircle,
  ChevronRight, Crown, Atom, HeartPulse, FlaskConical, Award, Database,
  Flame, Users,
} from "lucide-react";

// Iter 145be — Admin panel links surfaced inside /account when user.is_admin is true.
const ADMIN_PANEL_LINKS = [
  { to: "/admin/users",      label: "User Management",         sub: "View / search / disable / reset / delete users",           icon: Users,       accent: "cyan" },
  { to: "/admin/engines",    label: "Engine Control Center",  sub: "Toggle Classic / Quantum, per-market routing, performance", icon: Atom,        accent: "cyan" },
  { to: "/admin/health",     label: "System Health",           sub: "Cron jobs, queue depths, API rate-limit dashboards",       icon: HeartPulse,  accent: "emerald" },
  { to: "/admin/data-gaps",  label: "Data Gaps",               sub: "Settlement / fixture-sync gap supervisor",                 icon: Database,    accent: "amber" },
  { to: "/admin/nudge-ab",   label: "Nudge A/B Tests",         sub: "Tier upgrade nudge experiments",                           icon: FlaskConical, accent: "violet" },
  { to: "/admin/sponsors",   label: "Sponsors",                sub: "Manage advertiser slots + creatives",                      icon: Award,       accent: "rose" },
];

const STATUS_TONE = {
  pending:  "bg-warning/15 text-warning border-warning/40",
  running:  "bg-primary/15 text-primary border-primary/40",
  placed:   "bg-success/15 text-success border-success/40",
  failed:   "bg-destructive/15 text-destructive border-destructive/40",
};

const TABS = [
  { value: "profile",       label: "Profile",       icon: UserCircle },
  { value: "betway",        label: "Betway",        icon: KeyRound },
  { value: "strategy",      label: "Strategy",      icon: Flame },
  { value: "notifications", label: "Notifications", icon: Bell },
  { value: "system",        label: "System",        icon: Wrench },
];

const AccountPage = () => {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [linked, setLinked] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [betwayUrl, setBetwayUrl] = useState("");
  const [usernameMasked, setUsernameMasked] = useState(null);
  const [storedBetwayUrl, setStoredBetwayUrl] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [parlays, setParlays] = useState([]);
  const [diagnosing, setDiagnosing] = useState(false);
  const [diagReport, setDiagReport] = useState(null);

  // Tab state persisted to ?tab= so deep-links work.
  const activeTab = useMemo(() => {
    const t = searchParams.get("tab");
    return TABS.some((tab) => tab.value === t) ? t : "profile";
  }, [searchParams]);
  const setActiveTab = (val) => {
    const sp = new URLSearchParams(searchParams);
    if (val === "profile") sp.delete("tab"); else sp.set("tab", val);
    setSearchParams(sp, { replace: true });
  };

  useEffect(() => { if (!loading && !user) navigate("/auth"); }, [loading, user, navigate]);

  const fetchAccount = async () => {
    try {
      const { data } = await axios.get(`${API}/betway/account`);
      setLinked(!!data.linked);
      setUsernameMasked(data.username_masked || null);
      setStoredBetwayUrl(data.betway_url || null);
      setUpdatedAt(data.updated_at || null);
    } catch {}
  };

  const fetchParlays = async () => {
    try { setParlays((await axios.get(`${API}/betway/parlays?limit=20`)).data.bets || []); } catch {}
  };

  useEffect(() => {
    if (user) {
      fetchAccount();
      fetchParlays();
      // Only poll while the user is on tabs that surface parlay data.
      if (activeTab === "system" || activeTab === "profile") {
        const iv = setInterval(fetchParlays, 8000);
        return () => clearInterval(iv);
      }
    }
  }, [user, activeTab]);

  const saveBetway = async (e) => {
    e.preventDefault();
    if (!username || !password) return;
    setSaving(true);
    try {
      await axios.post(`${API}/betway/account`, { username, password, betway_url: betwayUrl || null });
      toast.success("Betway credentials saved (encrypted)");
      setUsername(""); setPassword(""); setBetwayUrl("");
      await fetchAccount();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to save credentials"); }
    finally { setSaving(false); }
  };

  const unlinkBetway = async () => {
    if (!window.confirm("Remove stored Betway credentials?")) return;
    try {
      await axios.delete(`${API}/betway/account`);
      toast.success("Credentials removed");
      setLinked(false); setUsernameMasked(null); setUpdatedAt(null);
    } catch { toast.error("Failed to remove credentials"); }
  };

  const runDiagnostic = async () => {
    setDiagnosing(true); setDiagReport(null);
    try {
      const { data } = await axios.post(`${API}/betway/diagnostic`);
      const diagId = data.diag_id;
      const started = Date.now();
      let done = null;
      while (Date.now() - started < 110000) {
        await new Promise((r) => setTimeout(r, 3000));
        try {
          const poll = await axios.get(`${API}/betway/diagnostic/${diagId}`);
          if (poll.data.status === "done") { done = poll.data.report; break; }
        } catch {}
      }
      if (!done) { toast.error("Diagnostic still running — try refreshing in a minute"); return; }
      setDiagReport(done);
      if (done.reachable && done.login_button_found && done.username_input_found) {
        toast.success("Betway is reachable");
      } else if (done.captcha_detected) { toast.warning("Betway served a CAPTCHA challenge"); }
      else { toast.error("Selector mismatch — see report below"); }
    } catch (err) { toast.error(err?.response?.data?.detail || "Diagnostic failed"); }
    finally { setDiagnosing(false); }
  };

  const onLogout = () => { logout(); toast.success("Logged out"); navigate("/"); };

  if (loading) {
    return (
      <div className="min-h-screen bg-background text-muted-foreground flex items-center justify-center">
        Loading your account…
      </div>
    );
  }
  if (!user) return null;

  const handle = (user.email || "").split("@")[0];

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="account-page">
      <TopNav />

      {/* Compact identity header — replaces the old 200px h1 block */}
      <header
        className="border-b border-border/60 bg-card/40"
        data-testid="account-header"
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-11 h-11 rounded-full bg-gradient-to-br from-amber-400 to-rose-500 text-background font-bold text-base flex items-center justify-center shrink-0">
              {(handle || "?")[0].toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="font-display text-xl font-bold tracking-tight truncate flex items-center gap-2">
                {handle}
                {user.is_admin && (
                  <span
                    className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.18em] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/40"
                    data-testid="account-admin-badge"
                    title="You have admin privileges"
                  >
                    <Crown className="w-3 h-3" />
                    Admin
                  </span>
                )}
              </div>
              <div className="text-xs text-muted-foreground truncate">{user.email}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {user.is_admin && (
              <Button
                onClick={() => navigate("/admin/engines")}
                data-testid="account-admin-panel-btn"
                variant="outline"
                className="h-9 rounded-full px-4 border-amber-500/40 bg-amber-500/10 hover:bg-amber-500/20 text-amber-200"
              >
                <ShieldCheck className="w-4 h-4 mr-1.5" /> Admin Panel
              </Button>
            )}
            <Button
              onClick={onLogout}
              data-testid="logout-btn"
              variant="outline"
              className="h-9 rounded-full px-4 border-border bg-card hover:bg-muted text-foreground"
            >
              <LogOut className="w-4 h-4 mr-1.5" /> Log out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Iter 145be — Admin panel quick-links, shown only to admins. */}
        {user.is_admin && (
          <section
            className="mb-6 rounded-2xl border border-amber-500/30 bg-gradient-to-br from-amber-500/5 via-transparent to-cyan-500/5 p-5"
            data-testid="account-admin-panel"
          >
            <header className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Crown className="w-4 h-4 text-amber-400" />
                <h2 className="font-display font-bold text-base tracking-tight">Admin Panel</h2>
              </div>
              <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-amber-300/80">
                Site-wide controls
              </span>
            </header>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {ADMIN_PANEL_LINKS.map((link) => {
                const Icon = link.icon;
                return (
                  <button
                    key={link.to}
                    onClick={() => navigate(link.to)}
                    data-testid={`account-admin-link-${link.to.replace(/\//g, "-").replace(/^-/, "")}`}
                    className="group text-left rounded-xl border border-border bg-card/50 hover:bg-card hover:border-amber-500/30 transition px-3 py-2.5 flex items-center gap-3"
                  >
                    <Icon className={`w-5 h-5 text-${link.accent}-400 shrink-0`} />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm truncate">{link.label}</div>
                      <div className="text-[11px] text-muted-foreground truncate">{link.sub}</div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition" />
                  </button>
                );
              })}
            </div>
          </section>
        )}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList
            className="bg-card/40 border border-border/60 p-1 h-auto flex flex-wrap gap-1"
            data-testid="account-tabs"
          >
            {TABS.map((tab) => {
              const Icon = tab.icon;
              return (
                <TabsTrigger
                  key={tab.value}
                  value={tab.value}
                  data-testid={`account-tab-${tab.value}`}
                  className="flex items-center gap-1.5 rounded-full px-3.5 py-2 text-xs font-bold uppercase tracking-[0.12em] data-[state=active]:bg-foreground data-[state=active]:text-background data-[state=inactive]:text-muted-foreground"
                >
                  <Icon className="w-3.5 h-3.5" />
                  {tab.label}
                </TabsTrigger>
              );
            })}
          </TabsList>

          {/* ── PROFILE ─────────────────────────────────────────────── */}
          <TabsContent value="profile" className="mt-6 space-y-4" data-testid="tab-profile">
            <ShortcutCard
              onClick={() => navigate("/account/earnings")}
              testid="account-earnings-link"
              icon={<Wallet className="w-5 h-5 text-emerald-400" />}
              tone="emerald"
              badge="NEW"
              title="Earnings Tracker"
              desc="Log daily P/L · monthly calendar · ROI · winning streaks · personal records"
            />
            <ShortcutCard
              onClick={() => navigate("/account/betslips")}
              testid="account-betslips-link"
              icon={<Sparkles className="w-5 h-5 text-amber-500" />}
              tone="amber"
              title="My betslip history"
              desc="Smart 5-leg · Curated 20-leg · Tonight's bonus — daily ROI tracker"
            />

            {/* Iter 136 — TV broadcaster country preference */}
            <TVCountryPicker />

            {/* Quick stats grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <StatTile label="Total parlays" value={parlays.length} />
              <StatTile
                label="Placed"
                value={parlays.filter((p) => p.status === "placed").length}
                tone="emerald"
              />
              <StatTile
                label="Pending"
                value={parlays.filter((p) => p.status === "pending" || p.status === "running").length}
                tone="amber"
              />
              <StatTile
                label="Failed"
                value={parlays.filter((p) => p.status === "failed").length}
                tone="rose"
              />
            </div>

            {/* Betway link state — surfaced compactly so users know to fill it in */}
            <div
              className={`rounded-2xl border p-4 flex items-center gap-3 ${
                linked
                  ? "border-success/30 bg-success/5"
                  : "border-warning/30 bg-warning/5"
              }`}
              data-testid="profile-betway-state"
            >
              {linked ? (
                <CheckCircle2 className="w-5 h-5 text-success shrink-0" />
              ) : (
                <AlertCircle className="w-5 h-5 text-warning shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold">
                  {linked ? "Betway account linked" : "Link your Betway account"}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5 truncate">
                  {linked
                    ? `${usernameMasked || "—"} · ${storedBetwayUrl || "default site"}`
                    : "Required to place parlays directly from picks."}
                </div>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setActiveTab("betway")}
                data-testid="profile-betway-cta"
                className="h-8 rounded-full px-3 text-xs border-border bg-card hover:bg-muted shrink-0"
              >
                {linked ? "Manage" : "Set up"}
                <ChevronRight className="w-3.5 h-3.5 ml-1" />
              </Button>
            </div>
          </TabsContent>

          {/* ── BETWAY ──────────────────────────────────────────────── */}
          <TabsContent value="betway" className="mt-6 space-y-6" data-testid="tab-betway">
            <BetwayCredentialsCard
              linked={linked}
              username={username}
              setUsername={setUsername}
              password={password}
              setPassword={setPassword}
              betwayUrl={betwayUrl}
              setBetwayUrl={setBetwayUrl}
              usernameMasked={usernameMasked}
              storedBetwayUrl={storedBetwayUrl}
              updatedAt={updatedAt}
              saving={saving}
              onSave={saveBetway}
              onUnlink={unlinkBetway}
            />

            {/* Tested regions has its own titled card — render directly. */}
            <TestedRegions
              onUse={(url) => {
                setBetwayUrl(url);
                toast.success("URL pasted — fill creds and save above");
              }}
            />

            <CollapsibleCard
              title="Connection diagnostic"
              icon={<Activity className="w-5 h-5 text-muted-foreground" />}
              defaultOpen={false}
              testid="diagnostic-collapse"
            >
              <DiagnosticCard
                diagnosing={diagnosing}
                diagReport={diagReport}
                onRun={runDiagnostic}
              />
            </CollapsibleCard>
          </TabsContent>

          {/* ── STRATEGY (Module 2) ────────────────────────────────── */}
          <TabsContent value="strategy" className="mt-6 space-y-6" data-testid="tab-strategy">
            <StrategyPanel />
            <AliasReviewWidget />
          </TabsContent>

          {/* ── NOTIFICATIONS ───────────────────────────────────────── */}
          <TabsContent value="notifications" className="mt-6 space-y-6" data-testid="tab-notifications">
            <GoalAlertSettings />
            <EmailSubscriptions />
          </TabsContent>

          {/* ── SYSTEM ──────────────────────────────────────────────── */}
          <TabsContent value="system" className="mt-6 space-y-6" data-testid="tab-system">
            <ParlayHistoryCard parlays={parlays} onRefresh={fetchParlays} />
            <SanityFlags />
            <StreamManager />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};


// ─── Reusable bits ───────────────────────────────────────────────────

const ShortcutCard = ({ onClick, testid, icon, tone, badge, title, desc }) => {
  const ring = tone === "emerald"
    ? "border-emerald-500/30 bg-gradient-to-br from-emerald-500/[0.06] via-card to-card hover:from-emerald-500/[0.10]"
    : "border-amber-500/30 bg-gradient-to-br from-amber-500/[0.06] via-card to-card hover:from-amber-500/[0.10]";
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`w-full text-left rounded-2xl border p-4 flex items-center gap-4 transition-colors ${ring}`}
    >
      <div className="w-12 h-12 rounded-xl bg-foreground/5 border border-border/40 flex items-center justify-center shrink-0">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-display font-bold text-base">{title}</span>
          {badge && (
            <Badge className={`text-[10px] font-bold tracking-wider ${
              tone === "emerald" ? "bg-emerald-500 text-emerald-950" : "bg-amber-500 text-amber-950"
            }`}>{badge}</Badge>
          )}
        </div>
        <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>
      </div>
      <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
    </button>
  );
};

const StatTile = ({ label, value, tone }) => {
  const valueTone =
    tone === "emerald" ? "text-emerald-500"
    : tone === "amber" ? "text-amber-500"
    : tone === "rose"  ? "text-rose-500"
    : "text-foreground";
  return (
    <div className="rounded-xl border border-border bg-card/60 px-3.5 py-2.5">
      <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-bold">
        {label}
      </div>
      <div className={`font-display font-bold text-2xl tracking-tight mt-0.5 ${valueTone}`}>
        {value}
      </div>
    </div>
  );
};

const CollapsibleCard = ({ title, icon, children, defaultOpen = false, testid }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-2xl border border-border bg-card overflow-hidden" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-5 py-3.5 hover:bg-muted/30 transition-colors"
        data-testid={`${testid}-toggle`}
      >
        <div className="flex items-center gap-3">
          {icon}
          <span className="font-display text-base font-semibold">{title}</span>
        </div>
        <ChevronRight className={`w-4 h-4 text-muted-foreground transition-transform ${open ? "rotate-90" : ""}`} />
      </button>
      {open && (
        <div className="border-t border-border px-5 py-5">
          {children}
        </div>
      )}
    </div>
  );
};

// ─── Sub-cards extracted from old AccountPage to keep render fns tidy ──

const BetwayCredentialsCard = ({
  linked, username, setUsername, password, setPassword, betwayUrl, setBetwayUrl,
  usernameMasked, storedBetwayUrl, updatedAt, saving, onSave, onUnlink,
}) => (
  <section className="rounded-2xl border border-border bg-card p-5" data-testid="betway-credentials-card">
    <div className="flex items-center gap-3 mb-3 flex-wrap">
      <KeyRound className="w-5 h-5" />
      <h2 className="font-display text-lg font-semibold">Betway account</h2>
      {linked ? (
        <Badge className="bg-success/15 text-success border border-success/40">
          <CheckCircle2 className="w-3 h-3 mr-1" /> Linked
        </Badge>
      ) : (
        <Badge className="bg-warning/15 text-warning border border-warning/40">
          <AlertCircle className="w-3 h-3 mr-1" /> Not linked
        </Badge>
      )}
    </div>

    <p className="text-xs text-muted-foreground mb-4 leading-relaxed">
      Credentials are encrypted at rest with Fernet (AES-128) and only decrypted in memory when placing a bet — never logged.{" "}
      <span className="text-warning">Automated betting may violate Betway's ToS. Proceed at your own risk.</span>
    </p>

    {linked && (
      <div className="rounded-xl border border-border bg-subtle p-3.5 mb-4 text-sm space-y-1" data-testid="betway-linked-info">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <span className="text-muted-foreground">User: </span>
            <span className="font-tabular text-foreground">{usernameMasked}</span>
            {updatedAt && (
              <span className="ml-3 text-xs text-muted-foreground">
                · updated {new Date(updatedAt).toLocaleString()}
              </span>
            )}
          </div>
          <Button
            type="button"
            onClick={onUnlink}
            data-testid="betway-unlink-btn"
            variant="outline"
            className="h-8 rounded-full px-3 border-destructive/40 text-destructive hover:bg-destructive/10"
          >
            <Trash2 className="w-3.5 h-3.5 mr-1" /> Unlink
          </Button>
        </div>
        <div className="text-xs text-muted-foreground">
          <span>URL: </span>
          <span className="font-tabular text-foreground">{storedBetwayUrl || "default"}</span>
        </div>
      </div>
    )}

    <form onSubmit={onSave} className="space-y-3" data-testid="betway-creds-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className="eyebrow">Username / email</Label>
          <Input
            type="text" value={username} onChange={(e) => setUsername(e.target.value)}
            placeholder="your Betway login"
            data-testid="betway-username"
            className="mt-1.5 bg-subtle border-border h-10 rounded-xl"
          />
        </div>
        <div>
          <Label className="eyebrow">Password</Label>
          <Input
            type="password" value={password} onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            data-testid="betway-password"
            className="mt-1.5 bg-subtle border-border h-10 rounded-xl"
          />
        </div>
      </div>
      <div>
        <Label className="eyebrow">
          Site URL <span className="text-muted-foreground normal-case">(optional)</span>
        </Label>
        <Input
          type="text" value={betwayUrl} onChange={(e) => setBetwayUrl(e.target.value)}
          placeholder="e.g. https://www.betway.ca"
          data-testid="betway-url"
          className="mt-1.5 bg-subtle border-border h-10 rounded-xl"
        />
      </div>
      <Button
        type="submit"
        disabled={saving || !username || !password}
        data-testid="betway-save-btn"
        className="h-10 rounded-full px-5 bg-primary text-primary-foreground hover:bg-primary/90"
      >
        <ShieldCheck className="w-4 h-4 mr-2" />
        {saving ? "Saving…" : linked ? "Update credentials" : "Save encrypted credentials"}
      </Button>
    </form>
  </section>
);

const DiagnosticCard = ({ diagnosing, diagReport, onRun }) => (
  <div data-testid="diagnostic-card">
    <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
      <p className="text-xs text-muted-foreground max-w-md">
        Headless probe — no credentials are sent. Loads betway.com, opens the
        login form and reports which selectors matched.
      </p>
      <Button
        onClick={onRun}
        disabled={diagnosing}
        data-testid="run-diagnostic-btn"
        className="h-9 rounded-full px-4 bg-foreground text-background hover:bg-foreground/90"
      >
        <Activity className={`w-4 h-4 mr-2 ${diagnosing ? "animate-pulse" : ""}`} />
        {diagnosing ? "Probing" : "Run diagnostic"}
      </Button>
    </div>

    {diagReport && (
      <div className="space-y-3 text-sm" data-testid="diagnostic-report">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {[
            ["Reachable", diagReport.reachable],
            ["Login button", diagReport.login_button_found],
            ["Username input", diagReport.username_input_found],
            ["Password input", diagReport.password_input_found],
            ["Submit button", diagReport.submit_button_found],
            ["CAPTCHA", diagReport.captcha_detected],
          ].map(([label, ok]) => {
            const isCaptcha = label === "CAPTCHA";
            const good = isCaptcha ? !ok : !!ok;
            return (
              <div
                key={label}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${
                  good
                    ? "bg-success/10 border-success/40 text-success"
                    : "bg-destructive/10 border-destructive/40 text-destructive"
                }`}
              >
                {good ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                <span>{label}: {isCaptcha ? (ok ? "detected" : "clear") : (ok ? "ok" : "missing")}</span>
              </div>
            );
          })}
        </div>
        {diagReport.message && (
          <div className="rounded-lg border border-border bg-subtle p-3">{diagReport.message}</div>
        )}
        {diagReport.screenshot_b64 && (
          <details className="text-xs text-muted-foreground">
            <summary className="cursor-pointer">View Betway screenshot</summary>
            <img alt="Betway state" src={`data:image/jpeg;base64,${diagReport.screenshot_b64}`}
                 className="mt-2 rounded-lg border border-border max-h-96" />
          </details>
        )}
        {diagReport.dom_probe && (
          <details className="text-xs text-muted-foreground">
            <summary className="cursor-pointer">View DOM probe</summary>
            <pre className="mt-2 p-3 bg-subtle border border-border rounded-lg overflow-x-auto max-h-96 text-[11px]">
              {JSON.stringify(diagReport.dom_probe, null, 2)}
            </pre>
          </details>
        )}
      </div>
    )}
  </div>
);

const ParlayHistoryCard = ({ parlays, onRefresh }) => (
  <section className="rounded-2xl border border-border bg-card p-5" data-testid="parlay-history-card">
    <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
      <h2 className="font-display text-lg font-semibold">Recent parlays</h2>
      <Button
        onClick={onRefresh}
        variant="outline"
        data-testid="parlays-refresh-btn"
        className="h-8 rounded-full px-3.5 text-xs border-border bg-card hover:bg-muted text-foreground"
      >
        <Clock className="w-3.5 h-3.5 mr-1" /> Refresh
      </Button>
    </div>

    {parlays.length === 0 ? (
      <p className="text-sm text-muted-foreground" data-testid="parlays-empty">
        No parlays placed yet. From the home page, hit "Place Best Parlay" to
        auto-place your highest-confidence picks on Betway.
      </p>
    ) : (
      <ul className="divide-y divide-border -mx-1" data-testid="parlays-list">
        {parlays.map((p) => (
          <li key={p.id} className="px-1 py-3 flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <Badge className={`border ${STATUS_TONE[p.status] || "bg-muted text-muted-foreground border-border"}`}>
                  {p.status}
                </Badge>
                <span className="text-xs text-muted-foreground">{new Date(p.created_at).toLocaleString()}</span>
                <span className="text-xs text-muted-foreground">
                  · {p.selections?.length || 0} leg{(p.selections?.length || 0) === 1 ? "" : "s"}
                </span>
                <span className="text-xs text-muted-foreground">· stake {p.stake}</span>
                {p.action === "booking_code" && (
                  <span className="text-xs text-success">· booking only</span>
                )}
              </div>
              <p className="text-sm text-foreground truncate">
                <span className="text-muted-foreground">stage:</span> {p.stage} — {p.message}
              </p>
              {p.booking_code && (
                <p className="text-xs text-success mt-1">
                  Booking code: <code className="font-tabular">{p.booking_code}</code>
                </p>
              )}
              {p.bet_id_betway && (
                <p className="text-xs text-success mt-1">Betway receipt: {p.bet_id_betway}</p>
              )}
            </div>
          </li>
        ))}
      </ul>
    )}
  </section>
);

export default AccountPage;
